import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiUserCheck, FiLock, FiKey, FiInfo, FiHome, FiArrowRight, FiLoader, FiAlertCircle, FiPlay, FiMonitor } = FiIcons;

const ReportedLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { validateAccessCode } = useInvestigation();
  
  const [accessCode, setAccessCode] = useState('');
  const [lastName, setLastName] = useState(''); 
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Hent retur-parametere hvis de finnes
  const companyId = searchParams.get('c');
  const deptId = searchParams.get('dept');

  const handleBack = () => {
    if (companyId) {
      navigate(`/s/${companyId}${deptId ? `?dept=${deptId}` : ''}`);
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate network delay
    setTimeout(() => {
      const validation = validateAccessCode(accessCode.trim());
      
      if (validation.valid) {
        // Create a temporary session for the reported party
        const sessionData = {
          id: 'temp_reported_' + Date.now(),
          role: 'reported',
          accessCodeData: validation.data,
          reportId: validation.data.reportId
        };
        login(sessionData, 'reported');
        navigate('/reported/dashboard');
      } else {
        // Fallback for demo: Allow specific demo code
        if (accessCode === 'DEMO-123') {
          handleDemoLogin();
        } else {
          setError(validation.error || 'Ugyldig tilgangskode');
          setIsLoading(false);
        }
      }
    }, 800);
  };

  const handleDemoLogin = () => {
    setIsLoading(true);
    // Simulate login for demo purposes
    setTimeout(() => {
      const sessionData = {
        id: 'demo_reported',
        role: 'reported',
        accessCodeData: {
          sanitizedDescription: 'DETTE ER EN DEMO-SAK FOR VISNING.\n\nBeskrivelse av forholdet:\nDet er mottatt et varsel vedrørende hendelse på avdelingsmøtet den 15. mai. Det hevdes at du skal ha hevet stemmen og benyttet nedsettende karakteristikker om en kollega i andres påhør.\n\nSaken følges opp i henhold til bedriftens rutiner for forsvarlig arbeidsmiljø.',
          reportId: 'DEMO-SAK-2024'
        },
        reportId: 'DEMO-SAK-2024'
      };
      login(sessionData, 'reported');
      navigate('/reported/dashboard');
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md w-full"
      >
        <div className="bg-purple-50 p-8 text-center border-b border-purple-100">
          <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <SafeIcon icon={FiUserCheck} className="text-white text-3xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Innsyn i sak</h1>
          <p className="text-gray-600 mt-2 text-sm">
            For deg som er omvarslet i en HMS-sak. Bruk koden du har mottatt for å få innsyn og gi tilsvar.
          </p>
        </div>

        <div className="p-8">
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r text-sm flex items-start gap-2"
            >
              <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tilgangskode
              </label>
              <div className="relative">
                <SafeIcon icon={FiKey} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono uppercase tracking-wider"
                  placeholder="INS-XXXX-XXX"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Etternavn (Valgfritt)
              </label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ditt etternavn"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isLoading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 shadow-md disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  Verifiserer...
                </>
              ) : (
                <>
                  Få innsyn <SafeIcon icon={FiArrowRight} />
                </>
              )}
            </button>
          </form>

          {/* Demo Section */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2 text-blue-800">
                <SafeIcon icon={FiMonitor} />
                <h3 className="font-semibold text-sm">Demonstrasjon</h3>
              </div>
              <p className="text-xs text-blue-700 mb-3 leading-relaxed">
                Er du en potensiell kunde? Se hvordan innsynsløsningen fungerer for den omvarslede parten.
              </p>
              <button 
                type="button"
                onClick={handleDemoLogin}
                className="w-full bg-white text-blue-600 border border-blue-200 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiPlay} className="text-xs" />
                Start Demo
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
              <SafeIcon icon={FiInfo} className="text-gray-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600 leading-relaxed">
                Din aktivitet blir logget. Du har rett til kontradiksjon (å uttale deg) i saker som omhandler deg. Varslerens identitet kan være skjermet i henhold til gjeldende lovverk.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-8 py-4 text-center">
          <button 
            onClick={handleBack} 
            className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center justify-center gap-1 mx-auto"
          >
            <SafeIcon icon={FiHome} /> 
            {companyId ? 'Tilbake til varslingssiden' : 'Tilbake til forsiden'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ReportedLogin;